import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const TemplatePreviewModal = ({ isOpen, onClose, templates, highlightField, formData = {}, isDark = false }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (isOpen && templates && templates.length > 0) {
      // Set the first template as default
      setSelectedTemplate(templates[0]);
    }
  }, [isOpen, templates]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !templates || templates.length === 0) return null;

  const highlightContent = (content, fields, formData) => {
    if (!fields || !content) return content;
    
    let highlightedContent = content;
    
    // Handle both single field (string) and multiple fields (array)
    const fieldsArray = Array.isArray(fields) ? fields : [fields];

    const planTypeFullNames = {
      'SP': 'Strata Plan',
      'DP': 'Deposited Plan',
      'CA': 'Community Association',
    };

    const fieldMappings = {
      'meeting_location': formData.meetingLocation || '',
      'zoom_meeting_id': formData.zoomMeetingId || '',
      'zoom_meeting_password': formData.zoomMeetingPassword || '',
      'plan_number': formData.planNumber || '',
      'building_address': formData.buildingAddress || '',
      'senders_name': formData.name || '',
      'SP/DP/CA': formData.planType || '',
      'plan_type_full': planTypeFullNames[formData.planType] || planTypeFullNames['SP'],
      'name': formData.name || '',
      'strata_manager_name': formData.sm_name || '',
      'strata_manager_email': formData.sm_email || '',
      'strata_manager_phone': formData.sm_phone || '',
    };

    if (formData.mergeFieldsData && typeof formData.mergeFieldsData === 'object') {
      Object.keys(formData.mergeFieldsData).forEach(fieldName => {
        const fieldValue = formData.mergeFieldsData[fieldName];
        fieldMappings[fieldName] = fieldValue || '';

        if (fieldValue && typeof fieldValue === 'string' && fieldValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
          try {
            const dateObj = new Date(fieldValue);
            const day = dateObj.getDate().toString().padStart(2, '0');
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
            const year = dateObj.getFullYear();
            const hours = dateObj.getHours().toString().padStart(2, '0');
            const minutes = dateObj.getMinutes().toString().padStart(2, '0');

            fieldMappings[`${fieldName}_date`] = `${day}/${month}/${year}`;
            fieldMappings[`${fieldName}_time`] = `${hours}:${minutes}`;
          } catch (e) {
            fieldMappings[`${fieldName}_date`] = fieldValue;
            fieldMappings[`${fieldName}_time`] = fieldValue;
          }
        }
      });
    }
    
    fieldsArray.forEach(field => {
      if (field) {
      const fieldPattern = new RegExp(`\\{${field}\\}`, 'gi');
        const fieldValue = fieldMappings[field];
        const highlightStyle = isDark 
          ? 'background-color: #cd6d2c; color: #000000; padding: 2px 4px; border-radius: 3px; font-weight: bold;'
          : 'background-color: #ffeb3b; padding: 2px 4px; border-radius: 3px; font-weight: bold;';

      const replacement = fieldValue && fieldValue.toString().trim() !== ''
        ? `<span style="${highlightStyle}">${fieldValue}</span>`
        : `<span style="${highlightStyle}">{${field}}</span>`;

      highlightedContent = highlightedContent.replace(fieldPattern, replacement);
      }
    });
    

    Object.keys(fieldMappings).forEach(fieldKey => {
      const fieldValue = fieldMappings[fieldKey];
      if (fieldValue && fieldValue.toString().trim() !== '') {
        const fieldPattern = new RegExp(`\\{${fieldKey}\\}`, 'gi');
        highlightedContent = highlightedContent.replace(fieldPattern, fieldValue);
      }
    });
    
    return highlightedContent;
  };

  const getTemplateTypeLabel = (type, recipientType) => {
    const recipient = getRecipientTypeLabel(recipientType);
    switch (type) {
      case 'email':
        return `${recipient} Email Template`;
      case 'email_reminder':
        return `${recipient} Reminder Template`;
      case 'sms':
        return `${recipient} SMS Template`;
      default:
        return `${recipient} Template`;
    }
  };

  const getRecipientTypeLabel = (type) => {
    switch (type) {
      case 'owner':
        return 'Owner';
      case 'owner-agent':
        return 'Agent';
      case 'tenant':
        return 'Tenant/Resident';
      default:
        return type;
    }
  };

  const getIconForTemplate = (type) => {
    if (type === 'sms') {
      return "/images/sms.svg";
    }
    return "/images/email.svg";
  };

  const getModalTitle = () => {
    if (!selectedTemplate) return "Template Preview";
    
    if (selectedTemplate.type === 'sms') {
      return "SMS Template Preview";
    }
    return selectedTemplate.type === 'email_reminder' ? "Email Reminder Template" : "Email Template Preview";
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-3 sm:p-4 lg:p-[16px]"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        className={`relative w-full sm:w-[90vw] lg:w-[929px] max-w-[929px] rounded-[12px] sm:rounded-[16px] shadow-lg flex flex-col max-h-[95vh] sm:max-h-[90vh] transition-colors duration-200 ${
          isDark ? 'bg-[#161515]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-3 sm:p-4 lg:p-[20px] border-b transition-colors duration-200 ${
          isDark ? 'border-[#292929]' : 'border-[#d9d9d9]'
        }`}>
          <div className="flex items-center gap-2 sm:gap-[8px]">
            <div
              className="w-5 h-5 sm:w-[24px] sm:h-[24px] shrink-0"
              style={{
                WebkitMaskImage: `url(${getIconForTemplate(selectedTemplate?.type)})`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: `url(${getIconForTemplate(selectedTemplate?.type)})`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: isDark ? '#b3b3b3' : '#072837',
                transition: 'background-color 0.2s ease'
              }}
              aria-label="Template type"
            />
            <h2 className={`text-[17px] sm:text-[20px] font-semibold m-0 transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>
              {getModalTitle()}
            </h2>
          </div>
          <button
            className="w-6 h-6 sm:w-[24px] sm:h-[24px] flex items-center justify-center cursor-pointer bg-transparent border-0 p-0 shrink-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className={`w-5 h-5 sm:w-[20px] sm:h-[20px] transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`} />
          </button>
        </div>

        {/* Template Selection Controls */}
        {templates.length > 1 && (
          <div className={`p-3 sm:p-4 lg:p-[16px] border-b transition-colors duration-200 ${
            isDark ? 'border-[#292929]' : 'border-[#d9d9d9]'
          }`}>
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-[16px] flex-wrap">
              <p className={`text-[13px] sm:text-[14px] font-semibold m-0 transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>Template Type:</p>
              <div className="flex gap-2 sm:gap-[8px] flex-wrap">
                {templates.map(template => (
                  <button
                    key={`${template.type}_${template.recipientType}`}
                    className={`px-2.5 py-1.5 text-[13px] sm:px-[12px] sm:py-[6px] sm:text-[15px] lg:text-[16px] font-medium border-solid border-[1px] rounded-[6px] cursor-pointer transition-all duration-200 ${
                      selectedTemplate?.type === template.type && selectedTemplate?.recipientType === template.recipientType
                        ? isDark
                          ? 'bg-[#cd6d2c] text-[#000000] border-[#cd6d2c]'
                          : 'bg-[#072837] text-white border-[#072837]'
                        : isDark
                          ? 'bg-[#161515] text-[#cd6d2c] border-[#cd6d2c] hover:bg-[#cd6d2c] hover:text-[#000000] '
                          : 'bg-white text-[#072837] border-[#d9d9d9] hover:bg-[#f5f5f5]'
                    }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    {getTemplateTypeLabel(template.type, template.recipientType)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Meta Info */}
        <div className={`p-3 sm:p-4 lg:p-[20px] border-b transition-colors duration-200 ${
          isDark ? 'border-[#292929]' : 'border-[#d9d9d9]'
        }`}>
          <div className="flex items-center gap-1.5 sm:gap-[6px] flex-wrap">
            <span className={`text-[14px] sm:text-[15px] lg:text-[16px] font-semibold transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>Template Type:</span>
            <span className={`text-[14px] sm:text-[15px] lg:text-[16px] font-normal transition-colors duration-200 ${
              isDark ? 'text-[#b3b3b3]' : 'text-[#898989]'
            }`}>
              {getTemplateTypeLabel(selectedTemplate?.type, selectedTemplate?.recipientType)}
            </span>
          </div>
        </div>

        {/* Body Content */}
        <div className={`p-3 sm:p-4 lg:p-[16px] rounded-b-[12px] sm:rounded-b-[16px] overflow-y-auto flex-1 transition-colors duration-200 ${
          isDark ? 'bg-[#000000]' : 'bg-[#f5f5f5]'
        }`}>
          <div
            className={`text-[13px] sm:text-[14px] font-normal leading-[1.5] whitespace-pre-wrap transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#444]'
            }`}
            dangerouslySetInnerHTML={{
              __html: highlightContent(selectedTemplate?.content || '', highlightField, formData)
            }}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TemplatePreviewModal;
